import {Component, Input} from '@angular/core';
import {PagingComponent} from "../../../../../components/paging.component";
import {
  CategoryItem,
  Country,
  DEFAULT_PAGE_SIZE,
  DuplicateCandidate,
  Gender,
  League,
  LeagueCompetitor,
  LeagueCompetitorFilter,
  LeagueCompetitorsSortBy,
  LeagueEvents,
  ListSortOrder,
  MatchStatus,
  PagedFilter,
  Pages
} from "@model";
import {
  CompetitorDetailsModalComponent,
  CompetitorMatchDetailsModalComponent,
  DuplicateCandidatesModalComponent,
  FilterGroup,
  FilterGroupType,
  FilterItem
} from "@components";
import {TranslocoService} from "@ngneat/transloco";
import {LeagueCompetitorMatchingData, LeagueCompetitorsService} from "@services";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {forkJoin, Observable, of} from "rxjs";
import {getLanguageValue} from "@utility";

const SORT_BY_MAP: Record<string, LeagueCompetitorsSortBy> = {
  id: LeagueCompetitorsSortBy.ID,
  firstName: LeagueCompetitorsSortBy.NAME,
  lastName:LeagueCompetitorsSortBy.NAME,
  country:  LeagueCompetitorsSortBy.COUNTRY,
  sex: LeagueCompetitorsSortBy.SEX,
  categoryItemLabel: LeagueCompetitorsSortBy.CATEGORY,
  matchStatus: LeagueCompetitorsSortBy.MATCH_STATUS,
};

@Component({
  selector: 'app-league-competitors',
  templateUrl: './league-competitors.component.html',
  styleUrl: './league-competitors.component.scss',
  standalone: false,
})
export class LeagueCompetitorsComponent extends PagingComponent<LeagueCompetitor>{
  @Input() competitors: LeagueCompetitor[] = [];
  @Input() duplicates: DuplicateCandidate[] = [];
  @Input() league: League | undefined;
  @Input() leagueEvents: LeagueEvents | undefined;
  @Input() isAdmin: boolean = false;
  @Input() competitorsCountries: Country[] = [];
  @Input() categoryItems: CategoryItem[] = [];

  filterGroups: FilterGroup[] = [];
  filter: LeagueCompetitorFilter = new LeagueCompetitorFilter();


  constructor(
      private translocoService: TranslocoService,
      private leagueCompetitorsService: LeagueCompetitorsService,
      private modalService: NgbModal) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();
    this.handleExploreResult(this.competitors);
    this.buildFilteringParams();
  }

  protected fetchPage(page: number): Observable<LeagueCompetitor[]> {
    if (this.league?.id) {
      return this.leagueCompetitorsService.getLeagueCompetitors(this.league.id, this.createFilter(page), this.isAdmin);
    }
    else return of([]);
  }

  protected override afterSetItems(items: LeagueCompetitor[]): void {
    this.competitors = items;
  }

  protected override afterOnExplore(): void {
    this.scrollToTop();
  }

  openMatchDetails(competitor: LeagueCompetitor) {
    if (!competitor.id || (competitor.matchStatus !== MatchStatus.MERGED && competitor.matchStatus !== MatchStatus.MERGED_DUPLICATE)) return;
    this.leagueCompetitorsService.getMatchHistory(this.league!.id!, competitor.id!).subscribe(value => {
      if (!value) return;

      const modalRef = this.modalService.open(CompetitorMatchDetailsModalComponent, {
        centered: true,
        size: 'xl'
      });
      modalRef.componentInstance.matchingData = value;
      modalRef.componentInstance.league = this.league;

      modalRef.componentInstance.onSave.subscribe((detachList: LeagueCompetitor[]) => {
        this.leagueCompetitorsService.detachLeagueCompetitors(this.league!.id!, competitor.id!, detachList, () => {
          forkJoin([
            this.leagueCompetitorsService.getMatchHistory(this.league!.id!, competitor.id!),
            this.leagueCompetitorsService.getLeagueDuplicateCompetitorsCandidates(this.league!.id!)
          ] as const).subscribe(([matchHistory, duplicates]: [LeagueCompetitorMatchingData | undefined, DuplicateCandidate[]]) => {
            if (!matchHistory?.matchedTo && matchHistory?.matchedFrom?.length === 0) {
              modalRef.close();
            } else {
              modalRef.componentInstance.matchingData = matchHistory;
            }
            this.duplicates = duplicates;
          });
          this.onExplore();
        }, () => {});
      });
    });
  }

  openCompetitorDetails(competitor: LeagueCompetitor) {
    const modalRef = this.modalService.open(CompetitorDetailsModalComponent, {
      centered: true,
      size: 'xl'
    });
    modalRef.componentInstance.competitor = competitor;
    modalRef.componentInstance.raceNames = this.initRaceNames();
  }


  openDuplicatesModal() {
    const modalRef = this.modalService.open(DuplicateCandidatesModalComponent, {
      centered: true,
      size: 'xl'
    });
    modalRef.componentInstance.duplicates = [...this.duplicates];
    modalRef.componentInstance.raceNames = this.initRaceNames();

    modalRef.componentInstance.onSave.subscribe((candidate: DuplicateCandidate) => {
      this.leagueCompetitorsService.resolveDuplicate(this.league!.id!, candidate, () => {
        this.leagueCompetitorsService.getLeagueDuplicateCompetitorsCandidates(this.league!.id!).subscribe(val => {
          this.duplicates = val;
          modalRef.componentInstance.duplicates = [...this.duplicates];
          modalRef.componentInstance.reset();
        });
        this.onExplore();
      }, () => {});
    });
  }


  onFilterChange(result: Record<string, any>): void {
    this.filter = new LeagueCompetitorFilter(
        result['searchTerm'],
        result['gender'],
        result['status'],
        result['eventIds'],
        result['raceIds'],
        result['categories'],
        result['countries'],
        this.filter.sortBy,
        this.filter.sortOrder,
    );
    this.onExplore(false);
  }


  onSort({ sortBy, sortOrder }: { sortBy: any[] | undefined, sortOrder: ListSortOrder | undefined }): void {
    if (!sortBy?.length) return;

    const sortByEnum = SORT_BY_MAP[sortBy[0]];
    if (sortByEnum == undefined) return;

    this.filter.sortBy = sortByEnum;
    this.filter.sortOrder = sortOrder ?? ListSortOrder.ASC;
    this.onExplore(false);
  }

  private createFilter(page: number): PagedFilter<LeagueCompetitorFilter> {
    return new PagedFilter(new Pages(DEFAULT_PAGE_SIZE, page), this.filter);
  }


  private initRaceNames(): Map<number, string> {
    return this.leagueEvents?.getRaceNames() ?? new Map<number, string>();
  }

  private buildFilteringParams() {
    const lang = this.translocoService.getActiveLang();

    this.filterGroups = [
      new FilterGroup('status', 'status', 'league.competitor.match.status', FilterGroupType.MULTI_SELECT, false, [
        new FilterItem(MatchStatus.FULL_MATCH,        undefined, 'league.competitor.match.status.1', false),
        new FilterItem(MatchStatus.MERGED,            undefined, 'league.competitor.match.status.2', false),
        new FilterItem(MatchStatus.DUPLICATE,         undefined, 'league.competitor.match.status.3', false),
        new FilterItem(MatchStatus.MERGED_DUPLICATE,  undefined, 'league.competitor.match.status.4', false),
      ]),
      new FilterGroup('gender', 'gender', 'app.common.gender', FilterGroupType.SINGLE_SELECT, false, [
        new FilterItem(Gender.MALE,   undefined, 'app.common.gender.1', false),
        new FilterItem(Gender.FEMALE, undefined, 'app.common.gender.2', false),
      ]),
      ...( this.competitorsCountries.length ? [
        new FilterGroup('countries', 'countries', 'app.common.country', FilterGroupType.MULTI_SELECT, true,
            this.competitorsCountries.map(c => new FilterItem(c.id, undefined, `app.common.country.${c.id}`, false))
        )
      ] : []),
      ...( this.categoryItems.length ? [
        new FilterGroup('categories', 'categories', 'app.common.category', FilterGroupType.MULTI_SELECT, false,
            this.categoryItems.map(ci => new FilterItem(ci.id, ci.code, undefined, false))
        )
      ] : []),
      ...( this.leagueEvents?.events?.length ? [
        new FilterGroup('raceIds', 'raceIds', 'app.common.race', FilterGroupType.MULTI_SELECT, true,
            this.leagueEvents.events.flatMap(e =>
                e.racesList?.map(r => new FilterItem(
                    r.id!,
                    `${getLanguageValue(e.name, e.nameEng, e.nameIta, e.nameSlo, e.nameSr, e.nameBs, lang, e.name)}
               - ${getLanguageValue(r.name, r.nameEng, r.nameIta, r.nameSlo, r.nameSr, r.nameBs, lang, r.name)}`
                )) ?? []
            )
        )
      ] : []),
    ];
  }

  protected readonly MatchStatus = MatchStatus;
}
